import { useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { UIFilterCampaignKindType } from './types';
import { ChatStartersPaginationParams } from '@mm/state/controllers/ChatStartersController';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { CampaignsOverviewPaginationParams } from '@mm/state/controllers/CampaignsOverviewController';
import { useCampaignsFilters } from './use-campaigns-filter';
import {
  selectInvalidRecords,
  selectRecordsByUiFilterKind,
} from '@mm/state/selectors/campaignsOverviewSelectors';
import { mapUIFilterKindToCampaignOverviewKind } from '@mm/state/helpers/campaign-overview-helpers';

const DEFAULT_PER_PAGE_QTY = 8;
const DEFAULT_PAGE = 1;

export const useFetchCampaigns = () => {
  const {
    activeKind,
    setFilterKind,
    activeSearch,
    setSearchQuery,
    doSearch,
    setDoSearch,
  } = useCampaignsFilters();

  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const { retrieveAll, invalidateAll } = useCampaignsOverviewCRUDActions();

  const invalidRecords = useSelector(selectInvalidRecords);
  const records = useSelector((state: StoreState) =>
    selectRecordsByUiFilterKind(state, activeKind),
  );

  const fetchCallback = useCallback(
    ({
      uiFilterKind,
      pagination,
      search,
    }: {
      uiFilterKind: UIFilterCampaignKindType;
      pagination?: CampaignsOverviewPaginationParams;
      search?: string;
    }) => {
      const kind = mapUIFilterKindToCampaignOverviewKind(uiFilterKind);
      if (kind) {
        retrieveAll({
          botId: activeChatbotId,
          kind,
          search,
          pagination,
        });
      }
    },
    [activeChatbotId, retrieveAll],
  );

  const fetchCurrentActiveKind = useCallback(
    (pagination?: ChatStartersPaginationParams) => {
      fetchCallback({
        uiFilterKind: activeKind,
        search: activeSearch,
        pagination,
      });
    },
    [activeKind, activeSearch, fetchCallback],
  );

  const ref = useRef(debounce((fn: Function) => fn(), 1000));
  const fetchCurrentActiveKindThrottled = () =>
    ref.current(fetchCurrentActiveKind);

  const changePage = (pageNumber: number) => {
    if (pageNumber && records?.pagination) {
      fetchCurrentActiveKind({
        page: pageNumber,
        perPage: records.pagination.perPage,
      });
    }
  };

  useEffect(() => {
    fetchCurrentActiveKind({
      page: records?.pagination?.page || DEFAULT_PAGE,
      perPage: records?.pagination?.perPage || DEFAULT_PER_PAGE_QTY,
    });
  }, [activeKind]);

  useEffect(() => {
    if (invalidRecords[activeKind]) {
      fetchCurrentActiveKind({
        page: records?.pagination?.page || DEFAULT_PAGE,
        perPage: records?.pagination?.perPage || DEFAULT_PER_PAGE_QTY,
      });
    }
  }, [invalidRecords[activeKind]]);

  useEffect(() => {
    if (doSearch) {
      fetchCurrentActiveKindThrottled();
      setDoSearch(false);
    }
  }, [activeSearch]);

  useEffect(invalidateAll, [activeChatbotId]);

  // needed to retrieve / update counters for all groups
  const isKindInvalidated = !!invalidRecords[activeKind];
  useEffect(() => fetchCallback({ uiFilterKind: 'all' }), [isKindInvalidated]);

  return {
    records,
    setFilterKind,
    activeKind,
    changePage,
    activeSearch,
    setSearchQuery,
    fetchCurrentActiveKind,
  };
};
